import React, { useCallback, useContext } from "react";
import styled, { css } from "styled-components";
import WBCYearsContext from "../../../contexts/WBCYearsContext";
import { BestSelect } from "best-common-react";
import { isCurrentOrPastTournamentYear } from "../../../utils/WBCYearPredicates";
import ToggleButtons from "../../temp/ToggleButtons";
import MLBPlayerSummaryContext from "../../../contexts/MLBPlayerSummaryContext";

const FiltersContainer = styled.div.attrs(() => ({
  className: "d-flex justify-content-start"
}))`
  flex-direction: row;

  @media (max-width: 767px) {
    flex-direction: column;
  }

  @media print {
    width: 100%;
    flex-wrap: wrap;
  }
`;

const Filter = styled.div.attrs(() => ({
  className: "col-xs col-md-2 pl-0"
}))`
  @media (max-width: 767px) {
    margin-bottom: 0.75rem;
  }

  @media print {
    ${props =>
      props.$growOnPrint
        ? css`
            &&& {
              flex: 1 1 auto !important;
              flex-basis: auto !important;
              max-width: none !important;
              width: auto !important;
            }
          `
        : css``}
    ${props =>
      props.$fullRowOnPrint
        ? css`
            &&& {
              flex-basis: 100% !important;
              max-width: 100% !important;
              width: 100% !important;
            }
          `
        : css``}
  }
`;

const PositionsFilter = styled(Filter)`
  @media print {
    ${props =>
      props.$selectedCount > 1
        ? css`
            &&& {
              flex: 0 0 60%;
              max-width: 60%;
              min-width: 1.5in;
            }
            & > * {
              width: 100%;
            }
          `
        : css``}
  }
`;

const SortFilter = styled(Filter)`
  @media print {
    &&& {
      flex: 1 1 40% !important;
      max-width: 40% !important;
      width: auto !important;
      white-space: nowrap;
    }
  }
`;

const MLBPlayersByFilters = () => {
  const {
    positionFilter,
    positionFilterOptions,
    rosterListFilter,
    rosterListOptions,
    setPositionFilter,
    setRosterListFilter,
    setSortBy,
    sortBy,
    sortByOptions,
    tournamentTypeFilter,
    tournamentTypeOptions,
    setTournamentTypeFilter,
    DEFAULT_POSITIONS_FILTER_OPTION
  } = useContext(MLBPlayerSummaryContext);
  const { wbcYears, selectedWbcYear, changeSelectedYear } = useContext(WBCYearsContext);

  const yearOptions = wbcYears.filter(isCurrentOrPastTournamentYear);

  const handleWBCYearChange = useCallback(({ year }) => changeSelectedYear(year), [changeSelectedYear]);

  const handleSortByChange = sortByValue => {
    const sortBy = sortByOptions.find(option => option.value === sortByValue);
    setSortBy(sortBy);
  };

  // weird logic here but need to handle the case where all positions are selected and then another position is selected
  // this will then remove the all positions option
  // this will then keep the other position selected
  const handlePositionsFilterChange = positions => {
    if (!positions || positions.length === 0) {
      setPositionFilter([DEFAULT_POSITIONS_FILTER_OPTION]);
      return;
    }

    const hasAllPositions = positions.some(p => p.value === DEFAULT_POSITIONS_FILTER_OPTION.value);
    const otherPositions = positions.filter(p => p.value !== DEFAULT_POSITIONS_FILTER_OPTION.value);

    if (hasAllPositions && otherPositions.length > 0) {
      const lastSelected = positions[positions.length - 1];

      if (lastSelected.value === DEFAULT_POSITIONS_FILTER_OPTION.value) {
        positions = [lastSelected];
      } else {
        positions = otherPositions;
      }
    }

    setPositionFilter(positions);
  };

  return (
    <div className="form-group">
      <FiltersContainer>
        <Filter $growOnPrint={positionFilter && positionFilter.length > 1}>
          {selectedWbcYear && (
            <BestSelect
              name="yearsDropdown"
              value={selectedWbcYear}
              options={yearOptions}
              onChange={handleWBCYearChange}
            />
          )}
        </Filter>
        <Filter $growOnPrint={positionFilter && positionFilter.length > 1}>
          <BestSelect
            name="tournamentTypeDropdown"
            value={tournamentTypeFilter}
            options={tournamentTypeOptions}
            onChange={setTournamentTypeFilter}
          />
        </Filter>
        <Filter $growOnPrint={positionFilter && positionFilter.length > 1}>
          {rosterListFilter && (
            <BestSelect
              name="rosterListFilterDropdown"
              value={rosterListFilter}
              options={rosterListOptions}
              onChange={setRosterListFilter}
            />
          )}
        </Filter>
        <PositionsFilter $selectedCount={positionFilter ? positionFilter.length : 0}>
          {positionFilter && (
            <BestSelect
              name="positionFilterDropdown"
              value={positionFilter}
              options={positionFilterOptions}
              onChange={handlePositionsFilterChange}
              isMulti
            />
          )}
        </PositionsFilter>
        <SortFilter>
          {sortBy && (
            <ToggleButtons
              id="sort-player-summaries-by"
              value={sortBy}
              options={sortByOptions}
              onChange={handleSortByChange}
            />
          )}
        </SortFilter>
      </FiltersContainer>
    </div>
  );
};

export default MLBPlayersByFilters;
